import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { registerAndLoginUser, ResultLoginProps } from "../utils/user";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";
import useTokenStore from "../store/store";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import Footer from "../components/headerFooter/Footer";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsConditions from "./TermsConditions";
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/semantic-ui.css';
import '../styles/Userregister.css';
import SocialLoginButtons from "../components/SocialLoginButtons";
import TermsModal from "../components/TermsModal";
import ModalOverlay from "../components/ModalOverlay";
import jwt2json, { getTenantTokenForAppId } from "../utils/jwt2json";

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword?: string;
};

function Userregister() {
  const [loading, setLoading] = useState(false);
  const [, setFormSubmit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTermsAcceptModal, setShowTermsAcceptModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<Inputs | null>(null);
  const [message, setMessage] = useState<ResultLoginProps>({
    message: "",
    status: 0,
    token: "",
    user: {
      id: "",
      fullname: "",
      email: "",
      userHandle: "",
    },
  });
  const navigate = useNavigate();
  const setToken = useTokenStore((state) => state.setToken);

  const formSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("First Name is required")
      .min(3, "First Name must be at least 3 characters long"),
    lastName: Yup.string()
      .required("Last Name is required")
      .min(3, "Last Name must be at least 3 characters long"),
    email: Yup.string()
      .required("Email is required")
      .min(3, "Email must be at least 3 characters long")
      .email("Email is invalid"),
    phone: Yup.string()
      .required('Phone is required')
      .matches(/^\+?[1-9]\d{1,14}$/, 'Phone number is not valid'),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long"),
    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf([Yup.ref("password")], "Passwords do not match"),
  });

  const [showLeadingZeroModal, setShowLeadingZeroModal] = useState(false);

  const formOptions = { resolver: yupResolver(formSchema) };
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<Inputs>(formOptions);

  // When form is submitted, show T&C modal instead of directly submitting
  const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
    delete data.confirmPassword;

    // Ensure the phone number starts with a "+"
    if (!data.phone.startsWith('+')) {
      data.phone = '+' + data.phone;
    }

    // Store form data and show T&C modal
    setPendingFormData(data);
    setShowTermsAcceptModal(true);
  };

  // Actually submit the registration after T&C is accepted
  const handleTermsAccept = async () => {
    if (!pendingFormData) return;

    setShowTermsAcceptModal(false);
    setLoading(true);
    setFormSubmit(true);

    const response = await registerAndLoginUser(pendingFormData);
    setMessage(response);
    setLoading(false);
    setPendingFormData(null);

    if (response.status === 200) {
      setToken(response);
      setShowModal(true);
      setTimeout(() => {
        setShowModal(false);
        navigate("/listing");
      }, 3000);
    }
  };

  const handleTermsModalClose = () => {
    setShowTermsAcceptModal(false);
    setPendingFormData(null);
  };

  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );

  const getToken = useTokenStore((state) => state.token);
  useEffect(() => {
    if (getToken) {
      navigate("/listing");
    }
  }, [getToken, navigate]);

  useEffect(() => {
    const setBodyStyles = () => {
      document.body.style.display = "flex";
      document.body.style.flexDirection = "column";
      document.body.style.minHeight = "100vh";
      document.body.style.margin = "0";
      document.body.style.position = "relative";
      document.body.style.overflow = "auto";
    };

    setBodyStyles();

    return () => {
      document.body.style.display = "";
      document.body.style.flexDirection = "";
      document.body.style.minHeight = "";
      document.body.style.margin = "";
      document.body.style.position = "";
      document.body.style.overflow = "";
    };
  }, []);

  const isWhiteOrShade = (color: string) => {
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  return (
    <div
      className="userregister"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        position: "relative",
        overflow: "auto",
      }}
    >
      <Helmet>
        <title>Register | {getThemes && getThemes.name}</title>
      </Helmet>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "60px 60px",
          zIndex: -1,
        }}
      />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "4px",
          paddingTop: "160px",
          paddingBottom: "80px",
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white flex flex-wrap justify-center pt-8 pb-8 px-4"
          style={{
            width: "85%",
            maxWidth: "500px",
            border: "1px solid #ddd",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            zIndex: 1,
            backgroundColor: "white",
            marginBottom: "20px",
          }}
        >
          <div className="w-full">
            <p className="text-3xl font-roboto font-medium pb-8 pt-8 text-center">
              Register
            </p>

            {/* Social Login Buttons */}
            <SocialLoginButtons
              appId={(() => {
                const token = getTenantTokenForAppId();
                return token ? (jwt2json(token).appId as string) ?? "" : "";
              })()}
              color1={getThemes?.color1}
              color2={getThemes?.color2}
            />

            <div className="w-full py-2">
              <div className="grid grid-cols-2 gap-4 py-2">
                <div>
                  <label
                    htmlFor="first-name-input"
                    className="block text-base font-medium mb-2 text-left"
                    style={{
                      color: "black",
                      fontSize: "1rem",
                      fontWeight: "500",
                    }}
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first-name-input"
                    className="py-3 px-4 block w-full rounded-md text-sm"
                    style={{
                      border: "1px solid black",
                      borderRadius: "0.375rem",
                      padding: "0.75rem",
                      color: "#2D3748",
                      backgroundColor: "#FFF",
                    }}
                    placeholder="First Name"
                    {...register("firstName", { required: true })}
                  />
                  <small className="flex justify-start pt-3 text-red-hover-ce312b">
                    {errors.firstName && <span>{errors.firstName.message}</span>}
                  </small>
                </div>
                <div>
                  <label
                    htmlFor="last-name-input"
                    className="block text-base font-medium mb-2 text-left"
                    style={{
                      color: "black",
                      fontSize: "1rem",
                      fontWeight: "500",
                    }}
                  >
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last-name-input"
                    className="py-3 px-4 block w-full rounded-md text-sm"
                    style={{
                      border: "1px solid black",
                      borderRadius: "0.375rem",
                      padding: "0.75rem",
                      color: "#2D3748",
                      backgroundColor: "#FFF",
                    }}
                    placeholder="Last Name"
                    {...register("lastName", { required: true })}
                  />
                  <small className="flex justify-start pt-3 text-red-hover-ce312b">
                    {errors.lastName && <span>{errors.lastName.message}</span>}
                  </small>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 py-2">
                <div>
                  <label
                    htmlFor="phone-input"
                    className="block text-base font-medium mb-2 text-left"
                    style={{
                      color: 'black',
                      fontSize: '1rem',
                      fontWeight: '500',
                    }}
                  >
                    Phone No. *
                  </label>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid black',
                          borderRadius: '0.375rem',
                          backgroundColor: '#FFF',
                          padding: '0.25rem',
                          position: 'relative',
                        }}
                      >
                        <PhoneInput
                          country={'gb'}
                          value={value}
                          onChange={(phone: string, country: {} | CountryData) => {
                            if ('dialCode' in country) {
                              const countryCode = country.dialCode;
                              let phoneWithoutCountryCode = phone.slice(countryCode.length);

                              // Check for leading zero
                              if (phoneWithoutCountryCode.startsWith('0')) {
                                setShowLeadingZeroModal(true);
                                phoneWithoutCountryCode = phoneWithoutCountryCode.slice(1);
                              }

                              onChange(`+${countryCode}${phoneWithoutCountryCode}`);
                            } else {
                              onChange(phone);
                            }
                          }}
                          enableSearch={true}
                          inputStyle={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            fontSize: '1rem',
                            color: '#2D3748',
                            paddingLeft: '48px',
                          }}
                          inputProps={{
                            name: 'phone',
                            required: true,
                            placeholder: 'No leading 0 after country code',
                          }}
                          buttonStyle={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            position: 'absolute',
                            left: '3px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                          dropdownStyle={{
                            position: 'absolute',
                            top: 'calc(100% + 20px)',
                            left: -8,
                            borderRadius: '0.375rem',
                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #ddd',
                            backgroundColor: '#fff',
                          }}
                          dropdownClass="left-align-dropdown" // Ensure this matches your CSS
                          containerStyle={{
                            position: 'relative',
                          }}
                          searchStyle={{
                            margin: '0',
                            width: '85%',
                            height: '30px',
                            textAlign: 'left',
                          }}
                          disableCountryCode={false}
                          countryCodeEditable={false}
                        />
                      </div>
                    )}
                  />
                  <small className="flex justify-start pt-3 text-red-hover-ce312b">
                    {errors.phone && <span>{errors.phone.message}</span>}
                  </small>
                  <small className="flex justify-start pt-1 text-gray-500">
                    *Enter your number without a leading 0 after the country code.
                  </small>
                </div>

                 {/* Leading Zero Alert Modal */}
                  {showLeadingZeroModal && (
                    <ModalOverlay labelledBy="register-phone-format-title">
                        <h2 className="text-xl font-bold mb-4" id="register-phone-format-title">Phone Number Format</h2>
                        <p>Please enter your phone number without the leading zero after the country code.</p>
                        <p>If you have typed any other numbers then we have automatically removed it for you.</p>
                        <button
                          onClick={() => setShowLeadingZeroModal(false)}
                          style={{
                            width: "100px",
                            background: getThemes.color1,
                            color: getThemes.txtColor,
                            padding: "10px",
                            borderRadius: "5px",
                            border: "none",
                            cursor: "pointer",
                            transition: "background 0.3s ease",
                            marginTop: "20px",
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.background = getThemes.color2)}
                          onMouseOut={(e) => (e.currentTarget.style.background = getThemes.color1)}
                        >
                          OK
                        </button>
                    </ModalOverlay>
                  )}

                <div>
                  <label
                    htmlFor="email-input"
                    className="block text-base font-medium mb-2 text-left"
                    style={{
                      color: "black",
                      fontSize: "1rem",
                      fontWeight: "500",
                    }}
                  >
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email-input"
                    className="py-3 px-4 block w-full rounded-md text-sm"
                    style={{
                      border: "1px solid black",
                      borderRadius: "0.375rem",
                      padding: "0.75rem",
                      color: "#2D3748",
                      backgroundColor: "#FFF",
                    }}
                    placeholder="Email Address"
                    {...register("email", { required: true })}
                  />
                  <small className="flex justify-start pt-3 text-red-hover-ce312b">
                    {errors.email && <span>{errors.email.message}</span>}
                  </small>
                </div>
              </div>


              {/* Password and Confirm Password Fields */}
              <div className="grid grid-cols-2 gap-4 py-2">
                {/* Password */}
                <div>
                  <label
                    htmlFor="password-input"
                    className="block text-base font-medium mb-2 text-left"
                    style={{
                      color: "black",
                      fontSize: "1rem",
                      fontWeight: "500",
                    }}
                  >
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password-input"
                      className="py-3 px-4 block w-full rounded-md text-sm pr-10"
                      style={{
                        border: "1px solid black",
                        borderRadius: "0.375rem",
                        padding: "0.75rem",
                        color: "#2D3748",
                        backgroundColor: "#FFF",
                      }}
                      placeholder="Password"
                      {...register("password", { required: true })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ background: "transparent", border: "none" }}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  <small className="flex justify-start pt-3 text-red-hover-ce312b">
                    {errors.password && <span>{errors.password.message}</span>}
                  </small>
                </div>
                {/* Confirm Password */}
                <div>
                  <label
                    htmlFor="confirm-password-input"
                    className="block text-base font-medium mb-2 text-left"
                    style={{
                      color: "black",
                      fontSize: "1rem",
                      fontWeight: "500",
                    }}
                  >
                    Confirm Pass. *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirm-password-input"
                      className="py-3 px-4 block w-full rounded-md text-sm pr-10"
                      style={{
                        border: "1px solid black",
                        borderRadius: "0.375rem",
                        padding: "0.75rem",
                        color: "#2D3748",
                        backgroundColor: "#FFF",
                      }}
                      placeholder="Confirm Password"
                      {...register("confirmPassword", { required: true })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ background: "transparent", border: "none" }}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  <small className="flex justify-start pt-3 text-red-hover-ce312b">
                    {errors.confirmPassword && (
                      <span>{errors.confirmPassword.message}</span>
                    )}
                  </small>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            {message.status === 0 && (
              <div className="mt-4 text-center">
                <button
                  type="submit"
                  style={{
                    width: "30%",
                    background: isWhiteOrShade(getThemes?.color2) ? "black" : getThemes.color2,
                    color: isWhiteOrShade(getThemes.color1) ? "white" : getThemes.txtColor,
                    padding: "10px",
                    borderRadius: "5px",
                    border: "none",
                    cursor: "pointer",
                    transition: "background 0.3s ease",
                    boxSizing: "border-box",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = isWhiteOrShade(getThemes?.color1) ? "black" : getThemes.color1;
                    e.currentTarget.style.color = isWhiteOrShade(getThemes?.color1) ? "white" : getThemes.txtColor;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = isWhiteOrShade(getThemes?.color2) ? "black" : getThemes.color2;
                    e.currentTarget.style.color = isWhiteOrShade(getThemes.color1) ? "white" : getThemes.txtColor;
                  }}
                >
                  Sign Up
                  {loading && (
                    <span
                      className="animate-spin border-btnscolor inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full"
                      role="status"
                      aria-label="loading"
                      style={{ marginLeft: "10px" }}
                    />
                  )}
                </button>
                <Link to="/login">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center gap-2 font-medium tracking-wide hover:underline py-2"
                  >
                    Or if you are already a member, you can Log In Here
                  </button>
                </Link>
              </div>
            )}
          </div>
        </form>

        {/* T&C Acceptance Modal - shown when user clicks Sign Up */}
        <TermsModal
          isOpen={showTermsAcceptModal}
          onClose={handleTermsModalClose}
          onAccept={handleTermsAccept}
          color1={getThemes?.color1}
          color2={getThemes?.color2}
          actionText="Create Account"
        />

        {/* Success Modal */}
        {showModal && (
          <ModalOverlay labelledBy="register-complete-title">
              <h2 className="text-2xl font-bold mb-4" id="register-complete-title">Registration Complete</h2>
              <p>You have successfully registered and logged in.</p>
              <p className="mt-2 text-sm text-gray-600">
                Your notification and privacy preferences have been set to default values.
                You can customize them in your Settings page.
              </p>
              <p className="mt-2">Redirecting you to the listings page...</p>
          </ModalOverlay>
        )}

        {/* Error Modal */}
        {message.status !== 0 && message.status !== 200 && (
          <ModalOverlay labelledBy="register-error-title">
              <h2 className="text-xl font-bold mb-4" id="register-error-title">{message.message}</h2>
              <button
                onClick={() => setMessage({ ...message, status: 0 })}
                style={{
                  width: "100px",
                  background: getThemes.color1,
                  color: getThemes.txtColor,
                  padding: "10px",
                  borderRadius: "5px",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.3s ease",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = getThemes.color2)
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = getThemes.color1)
                }
              >
                OK
              </button>
          </ModalOverlay>
        )}

        {/* Terms & Conditions Viewer Modal (for reading full terms) */}
        {showTermsModal && (
          <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
            <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-4xl flex flex-col" style={{ backgroundColor: 'white' }}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-xl font-bold">Terms & Conditions</h3>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                <TermsConditions isModal={true} />
              </div>
            </div>
          </div>
        )}

        {/* Privacy Policy Modal */}
        {showPrivacyModal && (
          <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
            <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-4xl flex flex-col" style={{ backgroundColor: 'white' }}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-xl font-bold">Privacy Policy</h3>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                <PrivacyPolicy isModal={true} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Userregister;
